import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, Spinner } from 'reactstrap';
import {
  BackTo,
  Block,
  BlockContent,
  BlockDes,
  BlockHead,
  BlockTitle,
  Button,
  Icon,
  PreviewCard,
  Row
} from '../../components/Component';
import AuthFooter from './AuthFooter';
import Head from '../../layout/head/Head';
import LoadingPageLoader from '../loaders/LoadingPageLoader';
import RedirectingPageLoader from '../loaders/RedirectingPageLoader';
import { OptionCardLink } from '../components/cards/CardLinks';
import * as app_consts from "../../utils/app_consts";
import { SEND_SALES_REP_USER_ACCOUNT_OTP_API } from '../../api/api_data';
import {
  fail_send_sales_rep_user_account_otp,
  loading_send_sales_rep_user_account_otp,
  reset_send_sales_rep_user_account_otp,
  success_send_sales_rep_user_account_otp
} from '../../reducers/no_auth_tenant/send_sales_rep_user_account_otp_Slice';
import {
  SEND_TENANT_USER_ACCOUNT_OTP_ERROR_MSG,
  SEND_TENANT_USER_ACCOUNT_OTP_SUCCESS_MSG
} from '../../utils/error_success_messages';
import { messageToast } from '../../utils/Utils';
import { tenant_no_auth_actions_helper } from '../../actions/actions_helper';

// Enhanced Animation Variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      when: "beforeChildren"
    }
  },
  exit: { opacity: 0 }
};

const itemVariants = {
  hidden: { y: 30, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 10,
      mass: 0.5
    }
  }
};

const optionCardVariants = {
  hidden: { x: -50, opacity: 0 },
  visible: {
    x: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 120,
      damping: 12
    }
  },
  hover: {
    scale: 1.05,
    boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    transition: { duration: 0.2 }
  },
  tap: {
    scale: 0.98
  }
};

const buttonVariants = {
  hover: {
    scale: 1.05,
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    transition: { duration: 0.2 }
  },
  tap: {
    scale: 0.98,
    boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)"
  }
};

const initialState = { account_id: "", send_otp_option: "" };
const initialHideState = { type: "", status: false };

const WebStoreSentOTP = ({ tenant_info }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [error, setError] = useState("");
  const [formData, setFormData] = useState(initialState);
  const [pageData, setPageData] = useState(null);
  const [hideOption, setHideOption] = useState(initialHideState);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const sales_rep_info = useSelector((state) => state.set_masked_credentials.set_masked_credentials);
  const send_otp_listener = useSelector((state) => state.send_sales_rep_user_account_otp.send_sales_rep_user_account_otp);

  // Initialize page data
  useEffect(() => {
    if (sales_rep_info?.status === app_consts.SUCCESS && sales_rep_info.data)
    {
      setPageData(sales_rep_info.data);
      setFormData(prev => ({ ...prev, id: sales_rep_info.data?.id }));
    } else if (sales_rep_info?.status !== app_consts.LOADING)
    {
      navigate(`/${tenant_info?.url_prefix}/webstore/auth`);
    }
  }, [sales_rep_info, navigate, tenant_info]);

  // Handle OTP sending response
  useEffect(() => {
    if (send_otp_listener.status === app_consts.IDLE) return;

    setIsSubmitting(send_otp_listener.status === app_consts.LOADING);

    if (send_otp_listener.status === app_consts.FAIL)
    {
      setError(send_otp_listener.message);
      messageToast(send_otp_listener.message, app_consts.FAIL);
      setHideOption(initialHideState); // Reset hide option on failure
    } else if (send_otp_listener.status === app_consts.SUCCESS)
    {
      navigate(`/${tenant_info.url_prefix}/webstore/auth/verify_otp`);
    }

    if (send_otp_listener.status !== app_consts.LOADING)
    {
      dispatch(reset_send_sales_rep_user_account_otp());
    }
  }, [send_otp_listener, dispatch, navigate, tenant_info]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.send_otp_option)
    {
      setError("Please select an option to receive your OTP");
      return;
    }

    setError("");
    setHideOption({
      type: formData.send_otp_option.toLowerCase(),
      status: true
    });

    dispatch(tenant_no_auth_actions_helper(
      loading_send_sales_rep_user_account_otp,
      success_send_sales_rep_user_account_otp,
      fail_send_sales_rep_user_account_otp,
      SEND_TENANT_USER_ACCOUNT_OTP_ERROR_MSG,
      SEND_TENANT_USER_ACCOUNT_OTP_SUCCESS_MSG,
      app_consts.POST,
      `/${tenant_info?.url_prefix}/${SEND_SALES_REP_USER_ACCOUNT_OTP_API}`,
      formData
    ));
  };

  if (!pageData)
  {
    return sales_rep_info?.status === app_consts.LOADING ? (
      <LoadingPageLoader tenant_info={tenant_info} />
    ) : (
      <RedirectingPageLoader tenant_info={tenant_info} />
    );
  }

  return (
    <>
      <Head title={tenant_info?.name ? `${tenant_info.name} - Send OTP` : "Send OTP"} />

      <motion.div
        initial="hidden"
        animate="visible"
        exit="exit"
        variants={containerVariants}
        className="nk-block-middle nk-auth-body wide-xs"
      >
        {/* Brand Logo */}
        <motion.div
          variants={itemVariants}
          className="brand-logo pb-4 text-center"
        >
          <Link
            to={`/${tenant_info?.url_prefix}/auth`}
            className="logo-link"
          >
            {tenant_info?.tenant_logo && (
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                transition={{ type: "spring", stiffness: 400 }}
              >
                <img
                  className="logo-img logo-img-lg"
                  src={tenant_info.tenant_logo}
                  alt={`${tenant_info.name} logo`}
                  style={{ maxHeight: '80px' }}
                />
              </motion.div>
            )}
          </Link>
        </motion.div>

        {/* Main Card */}
        <motion.div variants={itemVariants}>
          <PreviewCard
            className="card-bordered"
            bodyClass="card-inner-lg"
            whileHover={{
              scale: 1.005,
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
            }}
          >
            <BlockHead>
              <BlockContent>
                {/* Back Button */}
                <motion.div variants={itemVariants}>
                  <BackTo
                    link={`/${tenant_info?.url_prefix}/webstore/auth`}
                    icon="arrow-left"
                    className="text-soft"
                  >
                    Back to login
                  </BackTo>
                </motion.div>

                {/* Title */}
                <motion.div variants={itemVariants}>
                  <BlockTitle
                    tag="h3"
                    className="text-center mb-2"
                    style={{
                      color: '#2c3e50',
                      fontWeight: '700',
                      fontSize: '1.5rem'
                    }}
                  >
                    {tenant_info?.name}
                  </BlockTitle>
                </motion.div>

                <motion.div variants={itemVariants}>
                  <BlockTitle
                    tag="h4"
                    className="text-center mb-4"
                    style={{
                      color: '#4a5568',
                      fontWeight: '500',
                      fontSize: '1.1rem'
                    }}
                  >
                    Webstore Sign In
                  </BlockTitle>
                </motion.div>

                {/* Subtitle */}
                <motion.div variants={itemVariants}>
                  <BlockTitle tag="h5" className="mb-2">
                    Send OTP{" "}
                    {pageData?.first_name && (
                      <span className="text-primary">
                        for {pageData.first_name} {pageData.last_name}
                      </span>
                    )}
                  </BlockTitle>
                </motion.div>

                {/* Description */}
                <motion.div variants={itemVariants}>
                  <BlockDes>
                    <p className="text-soft">Select where to receive your one-time login passcode:</p>
                  </BlockDes>
                </motion.div>
              </BlockContent>
            </BlockHead>

            {/* Error Alert */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="mb-4"
              >
                <Alert color="danger" className="alert-icon">
                  <Icon name="alert-circle" />
                  <span className="ml-2">{error}</span>
                </Alert>
              </motion.div>
            )}

            <form onSubmit={handleSubmit}>
              <Row className="g-4">
                {/* Mobile Option */}
                {pageData?.mobile_number &&
                  (hideOption.status ? hideOption.type === app_consts.SEND_OTP_MOBILE.toLowerCase() : true) && (
                    <motion.div
                      variants={optionCardVariants}
                      whileHover="hover"
                      whileTap="tap"
                      className="col-12"
                    >
                      <OptionCardLink
                        linkHeading="Mobile Number"
                        icon="mobile"
                        link_name={app_consts.SEND_OTP_MOBILE.toLowerCase()}
                        linkValue={`+${pageData.mobile_number}`}
                        setFormData={setFormData}
                        formData={formData}
                        disabled={isSubmitting}
                        selected={formData.send_otp_option === app_consts.SEND_OTP_MOBILE.toLowerCase()}
                      />
                    </motion.div>
                  )}

                {/* Email Option */}
                {pageData?.email_address &&
                  (hideOption.status ? hideOption.type === app_consts.SEND_OTP_EMAIL.toLowerCase() : true) && (
                    <motion.div
                      variants={optionCardVariants}
                      whileHover="hover"
                      whileTap="tap"
                      className="col-12"
                    >
                      <OptionCardLink
                        linkHeading="Email Address"
                        icon="mail"
                        link_name={app_consts.SEND_OTP_EMAIL.toLowerCase()}
                        linkValue={pageData.email_address}
                        setFormData={setFormData}
                        setHideOption={setHideOption}
                        formData={formData}
                        disabled={isSubmitting}
                        selected={formData.send_otp_option === app_consts.SEND_OTP_EMAIL.toLowerCase()}
                      />
                    </motion.div>
                  )}
              </Row>

              {/* Submit Button */}
              <motion.div
                variants={itemVariants}
                className="mt-4 pt-2"
              >
                <Button
                  as={motion.button}
                  variants={buttonVariants}
                  whileHover={!isSubmitting ? "hover" : {}}
                  whileTap={!isSubmitting ? "tap" : {}}
                  size="lg"
                  className="btn-block"
                  type="submit"
                  color="primary"
                  disabled={isSubmitting || !formData.send_otp_option}
                  style={{
                    borderRadius: '8px',
                    padding: '12px',
                    fontSize: '1rem',
                    fontWeight: '500'
                  }}
                >
                  {isSubmitting ? (
                    <>
                      <Spinner size="sm" color="light" className="mr-2" />
                      Sending Passcode...
                    </>
                  ) : (
                    "Send Passcode"
                  )}
                </Button>
              </motion.div>
            </form>
          </PreviewCard>
        </motion.div>
      </motion.div>

      <AuthFooter tenant_name={tenant_info?.name} />
    </>
  );
};

export default WebStoreSentOTP;